import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Shield, Search as SearchIcon, Download, AlertTriangle,
  Loader2, X, Trash2, FileJson, FileText as FileTextIcon,
  History, User as UserIcon, Mail, MessageSquare, FileCheck2,
} from "lucide-react";

const KIND_STYLE = {
  Access: "bg-navy/10 text-navy",
  Rectification: "bg-gold/30 text-navy",
  Erasure: "bg-red-100 text-red-700",
  Search: "bg-navy/5 text-navy/70",
};

export default function AdminGdpr() {
  const [tab, setTab] = useState("subject"); // "subject" | "history"
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmErase, setConfirmErase] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const search = async (e) => {
    e?.preventDefault();
    if (!email.trim()) { toast.error("Enter an email"); return; }
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/gdpr/admin/search", { email: email.trim() });
      setResult(r.data);
      const total = (r.data.submissions_count || 0) + (r.data.consents_count || 0);
      if (total === 0 && !r.data.user_account) {
        toast.info(`No personal data found for ${email}`);
      } else {
        toast.success(`Found ${total} records`);
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const doExport = async () => {
    setBusy(true);
    try {
      const r = await api.post("/gdpr/admin/export", { email: email.trim() });
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hawkam-gdpr-${email.replace(/[@.]/g, "_")}-${Date.now()}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Article-15 export downloaded (JSON)");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const doExportPdf = async () => {
    setBusy(true);
    try {
      const r = await api.post("/gdpr/admin/export?format=pdf",
        { email: email.trim() }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `hawkam-gdpr-${email.replace(/[@.]/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Article-15 export downloaded (PDF)");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const r = await api.get("/gdpr/admin/history?limit=200");
      setHistory(r.data || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoadingHistory(false); }
  };

  useEffect(() => { if (tab === "history") loadHistory(); }, [tab]);

  const doErase = async () => {
    setErasing(true);
    try {
      const r = await api.post("/gdpr/admin/erase", { email: email.trim(), confirm: true });
      toast.success(`Erasure complete — ${r.data.submissions_anonymised || 0} submissions anonymised, ${r.data.consents_scrubbed || 0} consents scrubbed.`);
      setConfirmErase(false);
      setResult(null);
      setEmail("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setErasing(false); }
  };

  return (
    <div data-testid="admin-gdpr">
      <span className="eyebrow">Data Protection</span>
      <h1 className="mt-2 text-3xl text-navy flex items-center gap-3">
        <Shield size={26} className="text-gold" /> GDPR Console
      </h1>
      <span className="divider-gold" />
      <p className="text-navy/70 text-sm max-w-2xl mb-6">
        Search, export, rectify, or erase personal data for any subject. Every action is written to the immutable audit log with your identity, IP, and timestamp.
      </p>

      <div className="flex gap-2 border-b border-navy/10 mb-6" role="tablist">
        <button
          data-testid="gdpr-tab-subject"
          onClick={() => setTab("subject")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "subject" ? "border-gold text-navy" : "border-transparent text-navy/60 hover:text-navy"
          }`}
        >
          <SearchIcon size={14} className="inline mr-1 text-gold" /> Subject Request
        </button>
        <button
          data-testid="gdpr-tab-history"
          onClick={() => setTab("history")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "history" ? "border-gold text-navy" : "border-transparent text-navy/60 hover:text-navy"
          }`}
        >
          <History size={14} className="inline mr-1 text-gold" /> DSR History
        </button>
      </div>

      {tab === "history" ? (
        <div data-testid="gdpr-history">
          {loadingHistory ? (
            <div className="text-navy/50 text-sm"><Loader2 className="animate-spin inline mr-2" /> Loading…</div>
          ) : history.length === 0 ? (
            <div className="card-elev p-8 text-navy/50 text-sm text-center">No DSR events yet.</div>
          ) : (
            <div className="card-elev overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-navy text-cream">
                  <tr>
                    <th className="text-start p-3">When</th>
                    <th className="text-start p-3">Type</th>
                    <th className="text-start p-3">Subject</th>
                    <th className="text-start p-3">Handled by</th>
                    <th className="text-start p-3">Status</th>
                    <th className="text-start p-3">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} data-testid={`dsr-row-${h.id}`} className="border-t border-navy/10 hover:bg-cream">
                      <td className="p-3 text-navy/70 text-xs whitespace-nowrap">{new Date(h.created_at).toLocaleString()}</td>
                      <td className="p-3"><Badge className={KIND_STYLE[h.kind] || ""}>{String(h.kind ?? "—")}</Badge></td>
                      <td className="p-3 text-navy/80">
                        {typeof h.subject_email === "string" && h.subject_email
                          ? h.subject_email
                          : <em className="text-navy/40">—</em>}
                      </td>
                      <td className="p-3 text-navy/80">
                        {String(h.handled_by ?? "system")}
                        <span className="text-navy/50 text-xs"> ({String(h.handled_by_role ?? "—")})</span>
                      </td>
                      <td className="p-3"><Badge className="bg-emerald-100 text-emerald-800">{String(h.status ?? "—")}</Badge></td>
                      <td className="p-3 text-navy/60 text-xs max-w-md truncate">
                        {h.details ? JSON.stringify(h.details).slice(0, 120) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <>

      <form onSubmit={search} className="card-elev p-6 flex flex-wrap items-end gap-3 mb-6">
        <div className="grow min-w-[260px]">
          <div className="text-xs uppercase tracking-widest text-navy/60 mb-2 flex items-center gap-2">
            <Mail size={12} className="text-gold" /> Data subject email
          </div>
          <input
            data-testid="gdpr-email"
            type="email"
            className="field-box"
            placeholder="subject@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <button data-testid="gdpr-search" type="submit" disabled={busy} className="btn-gold disabled:opacity-40">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <SearchIcon size={14} />}
          {busy ? "Searching…" : "Search"}
        </button>
      </form>

      {result && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="gdpr-result">
            <StatCard icon={UserIcon} label="Account" value={result.user_account ? "Yes" : "No"} tone={result.user_account ? "gold" : "muted"} />
            <StatCard icon={MessageSquare} label="Submissions" value={result.submissions_count} />
            <StatCard icon={FileCheck2} label="Consents" value={result.consents_count} />
            <StatCard icon={Mail} label="Replies received" value={result.replies_received_count} />
            <StatCard icon={Mail} label="Replies authored" value={result.replies_authored_count} />
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            <button
              data-testid="gdpr-export"
              onClick={doExport}
              disabled={busy}
              className="btn-outline-navy disabled:opacity-40"
            >
              <FileJson size={14} /> JSON
            </button>
            <button
              data-testid="gdpr-export-pdf"
              onClick={doExportPdf}
              disabled={busy}
              className="btn-outline-navy disabled:opacity-40"
            >
              <FileTextIcon size={14} /> PDF
            </button>
            <button
              data-testid="gdpr-erase-open"
              onClick={() => setConfirmErase(true)}
              disabled={busy}
              className="inline-flex items-center gap-2 border-2 border-red-500/50 text-red-600 hover:bg-red-500 hover:text-white px-4 py-2 text-sm rounded transition-colors"
            >
              <Trash2 size={14} /> Erase (Article 17)
            </button>
          </div>

          {/* Submissions */}
          {result.submissions.length > 0 && (
            <details open className="card-elev p-6 mb-6">
              <summary className="cursor-pointer flex items-center justify-between">
                <div className="text-lg text-navy">Submissions ({result.submissions.length})</div>
                <FileJson size={14} className="text-gold" />
              </summary>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-navy text-cream">
                    <tr>
                      <th className="text-start p-2">When</th>
                      <th className="text-start p-2">Type</th>
                      <th className="text-start p-2">Status</th>
                      <th className="text-start p-2">Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.submissions.map((s) => (
                      <tr key={s.id} className="border-t border-navy/10">
                        <td className="p-2 whitespace-nowrap text-navy/70 text-xs">{new Date(s.created_at).toLocaleDateString()}</td>
                        <td className="p-2"><Badge variant="secondary">{s.type}</Badge></td>
                        <td className="p-2"><Badge className="bg-gold text-navy">{s.status}</Badge></td>
                        <td className="p-2 text-navy/70 max-w-md truncate">{s.subject || s.message?.slice(0, 80) || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {/* Consents */}
          {result.consents.length > 0 && (
            <details className="card-elev p-6 mb-6">
              <summary className="cursor-pointer text-lg text-navy">Consents ({result.consents.length})</summary>
              <ul className="mt-4 space-y-2 text-sm">
                {result.consents.map((c) => (
                  <li key={c.id} className="border-t border-navy/10 pt-2 text-navy/70">
                    <span className="font-medium text-navy">{c.kind}</span> · {c.decision} · {new Date(c.created_at).toLocaleString()}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}

      {confirmErase && (
        <div className="fixed inset-0 bg-navy/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setConfirmErase(false)}>
          <div
            data-testid="gdpr-erase-modal"
            className="bg-white max-w-md w-full rounded-md border-2 border-red-500/40 p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="text-red-600 mb-2"><AlertTriangle size={28} /></div>
                <h2 className="text-2xl text-navy">Confirm Right-to-Erasure</h2>
              </div>
              <button onClick={() => setConfirmErase(false)} className="text-navy/50 hover:text-navy"><X size={18} /></button>
            </div>
            <span className="divider-gold" />
            <p className="text-navy/80 text-sm leading-relaxed">
              You are about to <b>anonymise all personal data</b> for <span className="font-mono text-red-700">{email}</span>. This action cannot be undone:
            </p>
            <ul className="text-sm text-navy/70 mt-3 space-y-1 list-disc ps-5">
              <li>Submissions will keep their structure but have name, phone, message replaced with <code>(erased)</code>.</li>
              <li>Consents will have their identifying fields scrubbed.</li>
              <li>Audit log entries are preserved (legal obligation).</li>
            </ul>
            <div className="mt-6 flex gap-3">
              <button
                data-testid="gdpr-erase-confirm"
                onClick={doErase}
                disabled={erasing}
                className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded font-semibold hover:bg-red-700 disabled:opacity-40"
              >
                {erasing ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                {erasing ? "Erasing…" : "Yes, erase now"}
              </button>
              <button onClick={() => setConfirmErase(false)} className="btn-outline-navy">Cancel</button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone = "navy" }) {
  return (
    <div className="card-elev p-4">
      <div className={tone === "gold" ? "text-gold" : "text-navy/50"}><Icon size={16} /></div>
      <div className="mt-2 text-2xl text-navy font-medium">{value ?? "—"}</div>
      <div className="text-[10px] uppercase tracking-widest text-navy/60">{label}</div>
    </div>
  );
}
